import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Card,
  CircularProgress,
  TextField,
  Typography,
  Snackbar,
  Alert,
  AppBar,
  Divider,
  Toolbar,
  Container,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { loginUser, useUserDispatch } from "../../context/UserContext";
import { resetUserPassword } from "../../app/resetPasswordSlice";
import { usePageTracking } from "../../usePageTracking";
import { getErrorFormattedMessage } from "../../util/util";
import backgroundImage from "./turing-backdrop.png";
import logo from "./Turing_Logo_Horizontal-Color_on_Dark.png";
import logoLight from "./Turing_Logo_Horizontal-Color_on_Light.png";
import TopClearLogo from "./Logo-TOP Clear Blue.png";

const StyledBox = styled(Box)(({ theme }) => ({
  minHeight: "100vh",
  display: "flex",
  flexDirection: "column",
  position: "relative", // Make it the containing block for absolutely positioned children
  overflow: "hidden",   // Ensure content stays within bounds
  // background: `linear-gradient(rgba(0, 0, 0, 0.3), rgba(0, 0, 0, 0.5)), url(${backgroundImage})`,
  // backgroundSize: "cover",
  // backgroundPosition: "center",
}));

const StyledCard = styled(Card)(({ theme }) => ({
  padding: theme.spacing(4),
  width: "100%",
  maxWidth: 400,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  backgroundColor: "rgba(255, 255, 255, 1)",
  backdropFilter: "blur(10px)",
  boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
  borderRadius: theme.shape.borderRadius * 4,
}));

const Logo = styled("img")({
  width: 145,
  marginLeft: 3,
});

const TCLogo = styled("img")({
  width: 180,
  align: "center" 
  //marginBottom: 0,
});

const NewLogin = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [loginValue, setLoginValue] = useState("");
  const [passwordValue, setPasswordValue] = useState("");
  const [forgotPasswordStatus, setForgotPasswordStatus] = useState(false);
  const [resetPasswordEmail, setResetPasswordEmail] = useState("");
  const [messageOpen, setMessageOpen] = useState(false);
  const [errorOpen, setErrorOpen] = useState(false);
  const [resetPasswordResponse, setResetPasswordResponse] = useState("");

  const dispatch = useDispatch();
  const userDispatch = useUserDispatch();
  const history = useHistory();

  usePageTracking();

  useEffect(() => {
    document.title = "Top Clear - Sewer Predict - Login";
  }, []);

  const resetPasswordStatus = useSelector(
    (state) => state.resetPasswordSlice.resetPasswordStatus
  );
  const resetPasswordError = useSelector(
    (state) => state.resetPasswordSlice.resetPasswordError
  );

  useEffect(() => {
    if (resetPasswordStatus && isLoading) {
      setResetPasswordResponse(resetPasswordStatus);
      setMessageOpen(true);
      setForgotPasswordStatus(false);
      setIsLoading(false);
    }
    if (resetPasswordError && isLoading) {
      const errorMessage = getErrorFormattedMessage(resetPasswordError);
      setResetPasswordResponse(errorMessage);
      setErrorOpen(true);
      setIsLoading(false);
    }
  }, [resetPasswordStatus, resetPasswordError, isLoading]);

  const handleLogin = async () => {
    if (loginValue.length > 0 && passwordValue.length > 0) {
      setIsLoading(true);
      try {
        await loginUser(
          userDispatch,
          loginValue,
          passwordValue,
          history,
          setIsLoading,
          setError
        );
      } catch (err) {
        setError("Login failed. Please try again.");
        setIsLoading(false);
      }
    }
  };

  const handleResetPassword = () => {
    dispatch(resetUserPassword({ email: resetPasswordEmail }));
    setIsLoading(true);
  };

  return (
    <StyledBox>
      <AppBar position="static" color="transparent" elevation={0}>
        <Logo style={{ padding: "15px" }} src={logo} alt="Turing" />
      </AppBar>
      <Box
        flex={1}
        display="flex"
        justifyContent="center"
        alignItems="center"
        // minHeight="80vh"
        padding={2}
        elevation={5}
      >
        {/* Video background */}
        <video
            autoPlay
            loop
            muted
            playsInline
            style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              minWidth: "100vw",
              // minHeight: "60vh",
              // objectFit: "cover",
              transform: "translate(-50%, -50%)",
              zIndex: -1, // Behind content
            }}
          >
            <source src={`assets/video/turingbackdrop-video.mp4`} type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        <StyledCard>
          <AppBar
            style={{ alignItems: "center" }}
            position="static"
            color="transparent"
            elevation={0}
          >
            <TCLogo src={TopClearLogo} alt="Turing" />
          </AppBar>
          <Typography variant="subtitle1" gutterBottom color="textSecondary">
            
          </Typography>
          <Divider flexItem sx={{margin: 2}} />
          {!forgotPasswordStatus ? (
            <>
              <TextField
                variant="outlined"
                margin="normal"
                required
                fullWidth
                id="email"
                label="Email Address"
                name="email"
                autoComplete="email"
                autoFocus
                value={loginValue}
                onChange={(e) => setLoginValue(e.target.value)}
              />
              <TextField
                variant="outlined"
                margin="normal"
                required
                fullWidth
                //name="password"
                label="Password"
                type="password"
                id="password"
                autoComplete="current-password"
                value={passwordValue}
                onChange={(e) => setPasswordValue(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === "Enter") {
                    handleLogin();
                  }
                }}
              />
              <Button
                fullWidth
                variant="contained"
                color="primary"
                size="large"
                onClick={handleLogin}
                disabled={isLoading}
                sx={{ mt: 2, mb: 2 }}
              >
                {isLoading ? <CircularProgress size={24} /> : "Sign In"}
              </Button>
              <Divider flexItem sx={{margin: 2}}/>
              <Button
                color="primary"
                onClick={() => setForgotPasswordStatus(true)}
              >
                Forgot Password?
              </Button>
              {/* <h6>Powered By Turing</h6> */}
            </>
          ) : (
            <>
              <Typography variant="body1" align="center" gutterBottom>
                Enter your email address to reset your password
              </Typography>
              <TextField
                variant="outlined"
                margin="normal"
                required
                fullWidth
                id="resetPasswordEmail"
                label="Email Address"
                name="email"
                autoComplete="email"
                value={resetPasswordEmail}
                onChange={(e) => setResetPasswordEmail(e.target.value)}
              />
              <Button
                fullWidth
                variant="contained"
                color="primary"
                size="large"
                onClick={handleResetPassword}
                disabled={isLoading || resetPasswordEmail.length === 0}
                sx={{ mt: 2, mb: 2 }}
              >
                {isLoading ? <CircularProgress size={24} /> : "Send Reset Link"}
              </Button>
              <Button
                color="primary"
                onClick={() => setForgotPasswordStatus(false)}
              >
                Back to Login
              </Button>
            </>
          )}
          {/* <Box
            width="100%"
            display="flex"
            flexDirection={{ xs: "column", lg: "row" }}
            justifyContent="center"
            alignItems="center"
            px={1.5}
            sx={{paddingTop: "50px"}}
          >
            <Box
              component="ul"
              sx={({ breakpoints }) => ({
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                justifyContent: "center",
                listStyle: "none",
                mt: 3,
                mb: 0,
                p: 0,
                fontSize: "0.6rem",
                // padding: "40px 5px 0px 5px",
                [breakpoints.up("lg")]: {
                  mt: 0,
                },
              })}
            >
               Powered By <Logo style={{ padding: "10px 5px 10px 5px", height: "17%", width:"17%" }} src={logoLight} alt="Turing" />
            </Box>
          </Box> */}
          
        </StyledCard>
      </Box>
      <Snackbar
        open={error !== null}
        autoHideDuration={3000}
        onClose={() => setError(null)}
      >
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      </Snackbar>
      <Snackbar
        open={messageOpen}
        autoHideDuration={3000}
        onClose={() => setMessageOpen(false)}
      >
        <Alert severity="success" onClose={() => setMessageOpen(false)}>
          {resetPasswordResponse}
        </Alert>
      </Snackbar>
      <Snackbar
        open={errorOpen}
        autoHideDuration={3000}
        onClose={() => setErrorOpen(false)}
      >
        <Alert severity="error" onClose={() => setErrorOpen(false)}>
          {resetPasswordResponse}
        </Alert>
      </Snackbar>
    </StyledBox>
  );
};

export default NewLogin;
